import { acceptingLabel, availabilityFreshnessCopy, waitBucketLabel } from "./availability";
import type { Facility } from "./public-directory";
import { cleanPracticeName } from "./structured-data";

/**
 * Display helpers shared by every listing surface (directory, specialty,
 * area and home cards, and the profile header) so a listing reads the same
 * everywhere.
 */
export type ListingTone = "neutral" | "ok" | "warn" | "urgent";

export function cleanText(raw: string | null | undefined): string | null {
  const value = raw?.trim();
  return value ? value : null;
}

/** Curated rows are always Canberra; imported rows must carry an ACT state or 26xx/29xx postcode. */
export function isCanberraFacility(facility: Facility): boolean {
  const source = facility.address_json.source;
  if (source === "cvlm-specialist-curated" || source === "cvlm-referral-hub") return true;
  const state = facility.address_json.state?.trim().toLowerCase();
  if (state) return state === "act" || state === "australian capital territory";
  const postcode = facility.address_json.postcode?.trim();
  if (!postcode) return true;
  return /^(26|29)\d{2}$/.test(postcode);
}

export function sourceDisplayName(source: string | undefined): string {
  if (source === "cvlm-specialist-curated") return "PulseBoard directory";
  if (source === "cvlm-referral-hub") return "PulseBoard referral hub";
  if (source === "apify-google-places") return "Google Places";
  if (source === "claimed-profile") return "Claimed profile";
  if (source === "practitioner-submitted") return "Practitioner submitted";
  if (!source) return "Directory";
  return source
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function formatSourceDate(raw: string | undefined | null): string | null {
  if (!raw) return null;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-AU", { day: "numeric", month: "short", year: "numeric" }).format(date);
}

/** "Checked 14 Oct 2026" / "Updated 2 Oct 2026" / null when no date is recorded. */
export function sourceCheckedLabel(facility: Facility): string | null {
  const date = formatSourceDate(facility.address_json.source_date);
  if (!date) return null;
  const source = facility.address_json.source;
  return source === "claimed-profile" || source === "practitioner-submitted" ? `Updated ${date}` : `Checked ${date}`;
}

export interface ListingAvailability {
  label: string;
  detail: string | null;
  freshness: string | null;
  tone: ListingTone;
  confirmed: boolean;
}

export function listingAvailability(facility: Pick<Facility, "availability">): ListingAvailability {
  const availability = facility.availability;
  if (!availability) {
    return { label: "Not confirmed", detail: null, freshness: null, tone: "neutral", confirmed: false };
  }
  const tone: ListingTone =
    availability.accepting === "yes" ? "ok" : availability.accepting === "urgent_only" ? "urgent" : "warn";
  const wait = waitBucketLabel(availability.waitBucket);
  return {
    label: acceptingLabel(availability.accepting),
    detail: wait ? `Typical wait ${wait}` : null,
    freshness: availabilityFreshnessCopy(availability),
    tone,
    confirmed: true,
  };
}

/** Sub-specialties first, then clinical interests, de-duplicated. */
export function listingFocus(facility: Facility): string[] {
  const json = facility.address_json as Facility["address_json"] & { clinical_interests?: unknown };
  const raw = [
    ...(facility.address_json.sub_specialties ?? []),
    ...(Array.isArray(json.clinical_interests) ? (json.clinical_interests as unknown[]) : []),
  ];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of raw) {
    if (typeof item !== "string") continue;
    const value = item.trim();
    const key = value.toLowerCase();
    if (!value || seen.has(key)) continue;
    seen.add(key);
    out.push(value);
  }
  return out;
}

export function listingPractice(facility: Facility): string | null {
  return cleanPracticeName(facility.address_json.practice_name) ?? cleanPracticeName(facility.practiceGroup?.name) ?? null;
}

export function listingTypeLabel(facilityType: Facility["facility_type"]): string {
  if (facilityType === "gp_practice") return "GP practice";
  if (facilityType === "specialist") return "Specialist";
  if (facilityType === "allied_health") return "Allied health";
  return facilityType;
}
